import { Box, Typography, IconButton } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import RecentOrdersTable from "../components/tables/RecentOrdersTable";
import StatBox from "../components/boxes/StateBox";
import PaymentsIcon from "@mui/icons-material/People";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WidgetsIcon from "@mui/icons-material/Widgets";
import GroupsIcon from "@mui/icons-material/Groups";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DownloadIcon from "@mui/icons-material/Download";
import Title from "../components/header/Title";
import AuthContext from "../store/auth-context";
import ProductService from "../services/ProductService";
import CategoryService from "../services/CategoryService";
import UserService from "../services/UserService";
import OrderService from "../services/OrderService";
import RefundService from "../services/RefundService";
import ProgressCircle from "../components/graphs/ProgressCircle";
import LineChart from "../components/graphs/LineChart";
import BarChart from "../components/graphs/BarChart";
import PieChart from "../components/graphs/PieChart";
import { theme } from "../theme";

const countFrom = (raw: any): number => {
  if (Array.isArray(raw)) return raw.length;
  if (raw?.totalElements != null) return raw.totalElements;
  if (Array.isArray(raw?.data)) return raw.data.length;
  if (Array.isArray(raw?.content)) return raw.content.length;
  return 0;
};

const Dashboard: React.FC = () => {
  const authCtx = useContext(AuthContext);
  const Item = styled(Paper)(({ theme }) => ({
    borderRadius: "12px",
    backgroundColor: "white",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    padding: "20px",
    textAlign: "start",
    color: theme.palette.text.secondary,
  }));

  const [stats, setStats] = useState({
    products: null as number | null,
    orders: null as number | null,
    categories: null as number | null,
    users: null as number | null,
    pendingRefunds: null as number | null,
  });
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    Promise.allSettled([
      ProductService.getProducts(),
      OrderService.getAllOrders(),
      CategoryService.getCategory(),
      UserService.getUsers(),

      RefundService.filterRefunds({ page: 0, size: 1 }),
    ]).then(([products, orders, categories, users, refunds]) => {
      setStats({
        products: products.status === "fulfilled" ? countFrom(products.value.data) : null,
        orders: orders.status === "fulfilled" ? countFrom(orders.value.data) : null,
        categories: categories.status === "fulfilled" ? countFrom(categories.value.data) : null,
        users: users.status === "fulfilled" ? countFrom(users.value.data) : null,
        pendingRefunds:
          refunds.status === "fulfilled"
            ? countFrom(refunds.value.data)
            : null,
      });
      if (
        [products, orders, categories, users, refunds].every(
          (r) => r.status === "rejected"
        )
      ) {
        setStatsError(
          "Live stats could not be loaded. Please check your connection to the API."
        );
      }
    });
  }, []);

  const statCard = (
    value: number | null,
    label: string,
    icon: React.ReactNode
  ) => (
    <Grid item xs={6} md={2.4}>
      <Item>
        <StatBox
          title={value === null ? "…" : String(value)}
          subtitle={label}
          increase=""
          icon={icon}
        >
          {value === null && <CircularProgress size={20} />}
        </StatBox>
      </Item>
    </Grid>
  );

  return (
    <Box>
      <Title
        title={`Hi! ${authCtx.user?.name || "Admin"}`}
        subtitle="Welcome to your dashboard"
      />

      {statsError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {statsError}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {statCard(stats.products, "Total Products", <Inventory2Icon />)}
        {statCard(stats.orders, "Total Orders", <LocalMallIcon />)}
        {statCard(stats.categories, "Total Categories", <WidgetsIcon />)}
        {statCard(stats.users, "Total Users", <GroupsIcon />)}
        {statCard(
          stats.pendingRefunds,
          "Refund Requests",
          <CurrencyExchangeIcon />
        )}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Item>
            <Box
              mt="25px"
              p="0 30px"
              display="flex "
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="h6" fontWeight="600" color="text.secondary">
                  Revenue Generated
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color={theme.palette.primary.main}
                >
                  $59,342.32
                </Typography>
              </Box>

              <Box>
                <IconButton>
                  <DownloadIcon
                    sx={{ fontSize: "26px", color: theme.palette.primary.main }}
                  />
                </IconButton>
              </Box>
            </Box>
            <Box height="250px" m="-20px 0 0 0">
              <LineChart isDashboard={true} />
            </Box>
          </Item>
        </Grid>
        <Grid item xs={12} md={4}>
          <Item>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              borderBottom={`3px solid ${theme.palette.primary.main}`}
              p="15px"
            >
              <Typography color="text.secondary" variant="h5" fontWeight="600">
                Recent Orders
              </Typography>
            </Box>
            <RecentOrdersTable variant="list" limit={5} />
          </Item>
        </Grid>
        <Grid item xs={12} md={4}>
          <Item>
            <Typography variant="h5" fontWeight="600">
              Campaign
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt="25px"
            >
              <ProgressCircle size="130" progress="0.55" />
              <Typography variant="h5" sx={{ mt: "15px" }}>
                $48,352 revenue generated
              </Typography>
              <Typography sx={{ textAlign: "center" }}>
                Includes extra misc expenditures and costs
              </Typography>
            </Box>
          </Item>
        </Grid>
        <Grid item xs={12} md={4}>
          <Item>
            <Typography variant="h5" fontWeight="600">
              Sales Quantity
            </Typography>
            <Box height="250px">
              <BarChart isDashboard={true} />
            </Box>
          </Item>
        </Grid>
        <Grid item xs={12} md={4}>
          <Item>
            <Typography variant="h5" fontWeight="600">
              Total Revenue
            </Typography>
            <Box height="250px">
              <PieChart />
            </Box>
          </Item>
        </Grid>
      </Grid>

      <Typography variant="h5" fontWeight="600" sx={{ mb: 1 }}>
        Recent Orders
      </Typography>
      <RecentOrdersTable variant="grid" />
    </Box>
  );
};

export default Dashboard;
